import React, { useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import * as DS from '@styles/DesignSystem.styles';
import { FiX } from 'react-icons/fi';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  closeOnOverlayClick?: boolean;
}

const Overlay = styled(DS.ModalOverlay)<{ isOpen: boolean }>`
  display: ${props => (props.isOpen ? 'flex' : 'none')};
  animation: fadeIn ${theme.transitions.normal};

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainerStyled = styled(DS.ModalContainer)<{ size: string }>`
  animation: slideUp ${theme.transitions.normal};

  width: ${props => {
    switch (props.size) {
      case 'sm':
        return '400px';
      case 'lg':
        return '800px';
      case 'xl':
        return '1200px';
      default:
        return '600px';
    }
  }};

  max-width: 95vw;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeaderStyled = styled(DS.ModalHeader)`
  padding: ${theme.spacing.lg};
  border-bottom: 1px solid ${theme.colors.surface};
`;

const ModalTitleStyled = styled(DS.ModalTitle)`
  font-size: ${theme.typography.sizes['2xl']};
  margin: 0;
`;

const CloseButtonStyled = styled(DS.ModalCloseButton)`
  color: ${theme.colors.text}80;

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.text};
  }
`;

const ModalBodyStyled = styled(DS.ModalBody)`
  padding: ${theme.spacing.lg};
`;

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'md',
  closeOnOverlayClick = true,
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <Overlay isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainerStyled size={size}>
        {title && (
          <ModalHeaderStyled>
            <ModalTitleStyled>{title}</ModalTitleStyled>
            <CloseButtonStyled onClick={onClose} aria-label="Close modal">
              <FiX size={24} />
            </CloseButtonStyled>
          </ModalHeaderStyled>
        )}
        <ModalBodyStyled>{children}</ModalBodyStyled>
      </ModalContainerStyled>
    </Overlay>
  );
};
